import { Form, Formik, FormikHelpers } from "formik";
import { useMemo, useState } from "react";
import * as Yup from "yup";
import { Button } from "@/core/components/ui/button";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack/stack";
import { toast } from "@/core/components/ui/toast";
import { IBusiness } from "@/business/business";
import { useSaveBusiness } from "@/business/query";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { useBusiness } from "@/core/business";
import BusinessPaymentsSettings from "./business-payments-settings";
import { BusinessServiceTitanSettings } from "./business-service-titan-settings";
// import BusinessReferralUpdateMessages from "./BusinessReferralUpdateMessages";
import BusinessSummary from "./business-summary";
import { GoogleBusinessConnection } from "./google-business-connection";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"),
  phone_number: Yup.string().required("Phone number is required"),
});

const Business = () => {
  const { selectedBusiness, businessId, isSpiffOnly } = useBusiness();

  const [isEditMode, setIsEditMode] = useState(false);

  const initialValues = useMemo<Partial<IBusiness>>(
    () => ({
      name: selectedBusiness?.name,
      credit_payment_enabled: selectedBusiness?.credit_payment_enabled,
      max_payout: selectedBusiness?.max_payout,
      phone_number: selectedBusiness?.phone_number,
      service_titan_enabled: selectedBusiness?.service_titan_enabled,
    }),
    [selectedBusiness],
  );

  const { mutate: saveBusiness } = useSaveBusiness(businessId);

  const handleSubmit = async (
    values: Partial<IBusiness>,
    { resetForm }: FormikHelpers<Partial<IBusiness>>,
  ) =>
    saveBusiness(values, {
      onSuccess: () => {
        resetForm({ values });
        toast.success("Business updated successfully");
        setIsEditMode(false);
      },
      onError: (error) => {
        toast.error("Failed to update business", {
          description:
            error instanceof Error ? error.message : "Please try again",
        });
      },
    });

  return (
    <Stack className="mr-5" gap="sm" fullWidth>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
        className="h-full"
      >
        {({ dirty, resetForm, isSubmitting }) => (
          <Stack className="h-full" gap="sm">
            <HeaderStack>
              <PageHeader>Business profile</PageHeader>
              <Stack direction="row" align="center">
                {!isEditMode ? (
                  <Button
                    size="sm"
                    type="button"
                    onClick={() => setIsEditMode(true)}
                    variant="secondary"
                  >
                    Edit
                  </Button>
                ) : (
                  <>
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => {
                        setIsEditMode(false);
                        resetForm();
                      }}
                      variant="secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      type="submit"
                      disabled={!dirty}
                      loading={isSubmitting}
                    >
                      Save
                    </Button>
                  </>
                )}
              </Stack>
            </HeaderStack>
            <MainCard>
              <Form className="h-full p-1">
                <ScrollArea className="h-full">
                  <MainStack className="gap-12 p-6 pt-5" showOverflow>
                    {!!selectedBusiness && (
                      <>
                        <BusinessSummary business={selectedBusiness} />
                        {!isSpiffOnly && (
                          <BusinessPaymentsSettings isEditMode={isEditMode} />
                        )}
                        <BusinessServiceTitanSettings
                          isEditMode={isEditMode}
                          setIsEditMode={setIsEditMode}
                        />
                        <GoogleBusinessConnection business={selectedBusiness} />
                        {/* TODO: send isEditMode to BusinessReferralUpdateMessages when ready */}
                        {/* <BusinessReferralUpdateMessages /> */}
                      </>
                    )}
                  </MainStack>
                </ScrollArea>
              </Form>
            </MainCard>
          </Stack>
        )}
      </Formik>
    </Stack>
  );
};

export default Business;
